/* global Fingerprint */
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {DeleteItemsApi} from '../../../../../reduxToolkit/Commons/CommonsApi';
import {base64ToBlob, base64urlToBase64,} from "../../../../../utils/Base64ToBlob";
import {DIGITAL_PERSON_DEVICE_MODEL} from "../Model/Models";
import {TABLES} from "../../../../../Apis/Tables";

/**
 * CaptureFingerApp is a React hook-like component that manages
 * the lifecycle of fingerprint capture using the DigitalPersona SDK.
 * <p>
 * It dynamically loads SDK scripts, initializes the fingerprint device,
 * captures and clears fingerprints, and handles user interactions such as
 * selecting and deleting fingerprints from the list.
 * </p>
 *
 * @param {function} setCapturedFingerprint - Setter for the captured fingerprint blob.
 * @param {function} setFingerprintSrc - Setter for the base64 string of the fingerprint.
 * @param {boolean} capturing - State indicating if fingerprint capturing is in progress.
 * @param {function} setCapturing - Setter for updating capture state.
 * @param {object} capturedFingerprint - The currently captured fingerprint.
 * @param {boolean} updatedFinger - Flag that triggers fingerprint reset.
 * @param {function} setFingerprintName - Setter for fingerprint name.
 * @param {function} setSelectedReader - Setter for the selected fingerprint reader ID.
 * @param {string} fingerprintSrc - The fingerprint source in base64.
 * @param {Array} fingerprintsList - List of registered fingerprints.
 * @param {function} setUpdated - Setter to refresh parent state when fingerprints change.
 * @param {function} setModified - Setter for tracking modifications count.
 *
 * @returns {object} - Methods and state variables exposed to the parent component.
 */
const CaptureFingerApp = ({
                              setCapturedFingerprint, setFingerprintSrc, capturing, setCapturing,
                              capturedFingerprint, updatedFinger, setFingerprintName, setSelectedReader,
                              fingerprintSrc, fingerprintsList, setUpdated, setModified
                          }) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [selectFingerprintsForDelete, setSelectFingerprintsForDelete] = useState([]);
    const [isAllCheckedFingerprints, setIsAllCheckedFingerprints] = useState(false);
    const [deleteFingerprintsShow, setDeleteFingerprintsShow] = useState(false);
    const [start, setStart] = useState(false);
    const [sdk, setSdk] = useState(null);

    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (!start) return;

        const scriptSources = [
            '/js/es6-shim.js',
            '/js/websdk.client.bundle.min.js',
            '/js/fingerprint.sdk.min.js'
        ];

        /**
         * Dynamically loads required SDK scripts and initializes
         * the fingerprint capture process.
         *
         * @async
         * @function loadScripts
         */
        const loadScripts = async () => {
            try {
                await Promise.all([
                    loadScript(scriptSources[0]),
                    loadScript(scriptSources[1]),
                    loadScript(scriptSources[2]),
                ]).then(() => {
                    /**
                     * FingerprintSdkTest is a wrapper class around the
                     * Fingerprint.WebApi SDK, handling acquisition events,
                     * device listing, and capture lifecycle.
                     *
                     * @class
                     */
                    let FingerprintSdkTest = (function () {
                        function FingerprintSdkTest() {
                            this.sdk = null;
                        }

                        /**
                         * Starts the SDK instance and listens for acquisition events.
                         *
                         * @method
                         */
                        FingerprintSdkTest.prototype.start = function () {
                            this.sdk = new Fingerprint.WebApi();
                            this.sdk.on('SamplesAcquired', function (event) {
                                if (event && event.samples) {
                                    const resp = JSON.parse(event.samples);
                                    const parsedData = JSON.parse(atob(resp[0].Data));
                                    const wsqBase64url = base64urlToBase64(parsedData.Data);

                                    setCapturedFingerprint(base64ToBlob(wsqBase64url, 'application/octet-stream'));
                                    setFingerprintSrc(wsqBase64url);

                                    sdk.stopCapture();
                                    setCapturing(false);
                                }
                            });

                            this.sdk.on('ErrorOccured', function (event) {
                                if (event && event.deviceUid && event.error) {
                                    toast.error(t('something_went_wrong_2'));
                                }
                            });
                        };

                        /**
                         * Retrieves the list of available fingerprint devices.
                         *
                         * @returns {Promise<Array>} A promise with the list of devices.
                         */
                        FingerprintSdkTest.prototype.getDeviceList = function () {
                            return this.sdk.enumerateDevices();
                        };

                        /**
                         * Gets detailed information about a specific device.
                         *
                         * @param {string} uid - Device UID.
                         * @returns {Promise<object>} Device information.
                         */
                        FingerprintSdkTest.prototype.getDeviceInfoWithID = function (uid) {
                            return this.sdk.getDeviceInfo(uid);
                        };

                        /**
                         * Starts fingerprint capture on a specific device.
                         *
                         * @param {string} id - Device ID to start capturing from.
                         */
                        FingerprintSdkTest.prototype.startCapture = function (id) {
                            if (!this.sdk) return;

                            this.sdk.startAcquisition(Fingerprint.SampleFormat.Compressed, id).then(
                                () => setCapturing(true),
                                (error) => {
                                    toast.error(error.message);
                                    setCapturing(false);
                                }
                            );
                        };

                        /**
                         * Stops the current fingerprint capture session.
                         */
                        FingerprintSdkTest.prototype.stopCapture = function () {
                            this.sdk.stopAcquisition().then(
                                () => setCapturing(false),
                                (error) => toast.error(error.message)
                            );
                        };

                        return FingerprintSdkTest;
                    })();

                    const aux = new FingerprintSdkTest();
                    aux.start();
                    aux.getDeviceList().then(async function (sucessObj) {
                        if (!sucessObj.length) {
                            toast.info(t('no_readers_available'));
                            aux.stopCapture();
                            setCapturing(false);
                            setStart(false);
                            return;
                        }

                        let opticalReaders = [];
                        let firstOpticalReader = null;

                        await Promise.all(
                            sucessObj.map(async (element) => {
                                try {
                                    const deviceInfo = await aux.getDeviceInfoWithID(element);

                                    if (deviceInfo.eDeviceTech === DIGITAL_PERSON_DEVICE_MODEL.OPTICAL) {
                                        const newReader = {
                                            id: element,
                                            name: Fingerprint.DeviceTechnology[deviceInfo.eDeviceTech],
                                        };

                                        opticalReaders.push(newReader);

                                        if (!firstOpticalReader) {
                                            firstOpticalReader = newReader;
                                        }
                                    }
                                } catch (error) {
                                    setCapturing(false);
                                }
                            }),
                        );

                        if (opticalReaders.length > 0 && firstOpticalReader) {
                            setSelectedReader(firstOpticalReader.id);
                            aux.startCapture(firstOpticalReader.id);
                        } else {
                            toast.info(t('no_readers_available'));
                            aux.stopCapture();
                            setCapturing(false);
                        }
                    });
                    setSdk(aux);
                });
            } catch (error) {
                setCapturing(false);
                toast.error(t('something_went_wrong_2'));
            }
        };

        loadScripts();

        return () => {
            if (sdk != null) sdk.stopCapture();
            removeScripts(scriptSources);
        };
    }, [start]);

    /**
     * Removes SDK script tags from DOM.
     *
     * @param {Array<string>} srcArray - List of script URLs to remove.
     */
    const removeScripts = (srcArray) => {
        srcArray.forEach((src) => {
            const script = document.querySelector(`script[src="${src}"]`);
            if (script) {
                document.body.removeChild(script);
            }
        });
    };

    /**
     * Dynamically loads a script into DOM.
     *
     * @param {string} src - Script URL.
     * @returns {Promise} Resolves when the script is loaded.
     */
    const loadScript = (src) => {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
        });
    };

    /**
     * Starts the fingerprint capture process.
     */
    const handleCaptureFinger = () => {
        setStart(true);
    };

    useEffect(() => {
        if (updatedFinger === true) {
            handleClearFingerprint();
        }
    }, [updatedFinger]);

    /**
     * Clears current fingerprint data and resets states.
     */
    const handleClearFingerprint = () => {
        setFingerprintName('');
        setSelectedReader('');
        setFingerprintSrc('');
        setStart(false);
    };

    useEffect(() => {
        if (capturedFingerprint) {
            sdk.stopCapture();
            setCapturing(false);
            toast.success(t('fingerprint_captured_successfully'));
            setStart(false);
        }
    }, [capturedFingerprint]);

    useEffect(() => {
        if (capturing === true) {
            toast.success(t('instructions_capture_fingerprint'));
        }
    }, [capturing]);

    /**
     * Handles selection/deselection of all fingerprints in the list.
     *
     * @param {Event} e - Checkbox change event.
     */
    const handleRemoveAllFingerprints = (e) => {
        setIsAllCheckedFingerprints(e.target.checked);
        if (e.target.checked) {
            const selectAllIds = fingerprintsList.map((item) => item?.id);
            setSelectFingerprintsForDelete(selectAllIds);
        } else {
            setSelectFingerprintsForDelete([]);
        }
    };

    /**
     * Deletes selected fingerprints from the backend.
     *
     * @param {Array} deleteItem - List of fingerprint IDs to delete.
     */
    const deleteSelectedFingerprints = (deleteItem) => {
        const tableName = TABLES.USER_IMAGE;
        const body = deleteItem;
        dispatch(DeleteItemsApi({ tableName, body })).then(() => {
            setSelectFingerprintsForDelete([]);
            setIsAllCheckedFingerprints(false);
            setUpdated(true);
            setModified(prev => prev+1);
        });
    };

    /**
     * Maps an access method ID to its corresponding label.
     *
     * @param {number} accessMethodId - Access method identifier.
     * @returns {string} - Localized access method name.
     */
    const getAccessMethodName = (accessMethodId) => {
        switch (accessMethodId) {
            case 2:
                return t('FINGERPRINT');
            case 7:
                return t('FINGERPRINT_PDA');
            case 14:
                return t('FINGERPRINT_ANSI');
            case 15:
                return t('FINGERPRINT_ISO');
        }
    };

    return {
        t, permission, DEV, enqueueSnackbar,
        isAllCheckedFingerprints, setSelectFingerprintsForDelete,
        selectFingerprintsForDelete,
        deleteFingerprintsShow, setDeleteFingerprintsShow,
        handleRemoveAllFingerprints: (e) => handleRemoveAllFingerprints(e),
        getAccessMethodName: (e) => getAccessMethodName(e),
        handleClearFingerprint: (e) => handleClearFingerprint(e),
        handleCaptureFinger: (e) => handleCaptureFinger(e),
        deleteSelectedFingerprints: (e) => deleteSelectedFingerprints(e)
    }
}

export default CaptureFingerApp;
